import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { MAX_MSG_LEN } from "../../../shared/chat.js";
import {
  drawSceneBackground,
  drawSceneTitle,
  fillRoundedRect,
  roundedRectPath,
} from "../../utils/draw";
import { drawCharacterAvatar } from "../../utils/avatar";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import type { ChatMessage } from "../systems/Chat";
import { TextInput } from "../systems/TextInput";
import type { GameContext, Scene } from "./Scene";

// Layout (360×640 virtual). A messenger-style log: grouped bubbles + a pill input.
const LIST_TOP = 58;
const LIST_BOTTOM = 584;
const LIST_H = LIST_BOTTOM - LIST_TOP;
const PAD = 12;
const INPUT_H = 38;
const INPUT_Y = VIRTUAL_HEIGHT - INPUT_H - 8;
const SEND = { x: VIRTUAL_WIDTH - 12 - INPUT_H, y: INPUT_Y, w: INPUT_H, h: INPUT_H };
const FIELD = { x: PAD, y: INPUT_Y, w: SEND.x - 8 - PAD, h: INPUT_H };
const JUMP = { x: VIRTUAL_WIDTH - 44, y: LIST_BOTTOM - 42, w: 32, h: 32 };

// Bubble metrics.
const MAX_BUBBLE_W = 256;
const AV = 30; // avatar diameter
const AV_GUT = AV + 6; // left gutter reserved for the avatar on others' bubbles
const BPADX = 11;
const BPADY = 8;
const LINE_H = 17;
const NAME_H = 16;
const TIME_H = 14;
const GROUP_GAP = 12;
const TIGHT_GAP = 4;
const DAY_H = 28;
const GROUP_WINDOW = 5 * 60 * 1000; // messages within 5 min from one author group together

// Theme.
const MINE_TOP = "#f5e9c8";
const MINE_BOT = "#ecce84";
const MINE_TEXT = "#3a2c10";
const OTHER_FILL = "#323c56";
const OTHER_TEXT = "#eaf0fb";
const TIME_COLOR = "#8893ab";

interface Block {
  msg: ChatMessage;
  mine: boolean;
  lines: string[];
  bubbleW: number;
  bubbleH: number;
  showName: boolean;
  showTime: boolean;
  showAvatar: boolean;
  day: string | null;
  top: number;
  height: number;
}

/**
 * Messenger-style chat surface reused by global chat, friend DMs, and clan chat.
 * Messages from consecutive same-author bursts group into stacks of bubbles —
 * yours gold and right-aligned, others' slate and left-aligned with an
 * initial-avatar, names and timestamps — over the shared navy theme. Polls the
 * channel every 5s while open + visible. `onBack` routes back to the caller.
 */
export class ChatScene implements Scene {
  private readonly input: TextInput;
  private scrollY = 0; // 0 = pinned to newest; >0 scrolls into history
  private maxScroll = 0;
  private dragLastY: number | null = null;
  private pollAccum = 0;
  private polling = false;
  private sending = false;
  private entered = false;
  private toast = "";
  private toastT = 0;

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly channel: string,
    private readonly title: string,
    private readonly onBack: () => void,
  ) {
    this.input = new TextInput(ctxMgr.renderer);
  }

  private get myName(): string {
    return this.ctxMgr.settings.playerName;
  }
  private get token(): string {
    return this.ctxMgr.settings.authToken;
  }

  enter(): void {
    this.entered = true;
    this.input.attach(FIELD, {
      maxLength: MAX_MSG_LEN,
      placeholder: "Message…",
      onSend: (v) => void this.doSend(v),
      radius: INPUT_H / 2,
      accent: "#f0d98a",
    });
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
    void this.reload(true);
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.input.dispose();
    this.ctxMgr.buttons.clear();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(dt: number): void {
    this.input.update(dt);
    if (this.toastT > 0) this.toastT -= dt;
    if (document.hidden) return;
    this.pollAccum += dt;
    if (this.pollAccum >= 5 && !this.polling && !this.sending) {
      this.pollAccum = 0;
      void this.reload(false);
    }
  }

  private async reload(initial: boolean): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) return;
    this.polling = true;
    await this.ctxMgr.chat.fetch(this.myName, this.token, this.channel);
    this.polling = false;
    if (!this.entered) return;
    void this.ctxMgr.chat.markRead(this.myName, this.token, this.channel);
    if (initial) this.scrollY = 0;
  }

  private async doSend(raw: string): Promise<void> {
    const body = raw.trim();
    if (!body || this.sending) return;
    this.sending = true;
    const res = await this.ctxMgr.chat.send(this.myName, this.token, this.channel, body);
    this.sending = false;
    if (res.ok) {
      this.input.clear();
      this.input.focus();
      this.scrollY = 0; // jump to newest
      void this.ctxMgr.chat.markRead(this.myName, this.token, this.channel);
    } else {
      this.toast =
        res.error === "rate_limited"
          ? "Slow down a moment…"
          : res.error === "blocked"
            ? "Message blocked."
            : res.error === "forbidden"
              ? "You can't post here."
              : "Couldn't send.";
      this.toastT = 2;
    }
  }

  private messages(): ChatMessage[] {
    return this.ctxMgr.chat.messages(this.channel);
  }

  private rebuild(): void {
    const buttons: Button[] = [
      backButton(() => this.onBack()),
      // Tapping the field focuses the hidden input (needed for mobile keyboards).
      { ...FIELD, draw: () => undefined, onPress: () => this.input.focus() },
      {
        ...SEND,
        draw: (ctx, hit) => drawSendButton(ctx, hit),
        onPress: () => this.input.send(),
      },
      {
        ...JUMP,
        // Only visible/meaningful when scrolled up; harmless at the bottom.
        draw: (ctx, hit) => {
          if (this.scrollY > 24) drawJumpButton(ctx, hit);
        },
        onPress: () => {
          this.scrollY = 0;
        },
      },
    ];
    this.ctxMgr.buttons.set(buttons);
  }

  // Lay every message out into a positioned bubble block (grouped by author burst).
  private layout(ctx: CanvasRenderingContext2D): { blocks: Block[]; contentH: number } {
    const msgs = this.messages();
    const blocks: Block[] = [];
    const me = this.myName.toLowerCase();
    let yAcc = 0;
    ctx.font = "13px system-ui, sans-serif";
    for (let i = 0; i < msgs.length; i += 1) {
      const m = msgs[i]!;
      const prev = msgs[i - 1];
      const next = msgs[i + 1];
      const mine = m.author.toLowerCase() === me;
      const newDay = !prev || dayKey(prev.ts) !== dayKey(m.ts);
      const groupStart =
        newDay ||
        !prev ||
        prev.author.toLowerCase() !== m.author.toLowerCase() ||
        m.ts - prev.ts > GROUP_WINDOW;
      const groupEnd =
        !next ||
        dayKey(next.ts) !== dayKey(m.ts) ||
        next.author.toLowerCase() !== m.author.toLowerCase() ||
        next.ts - m.ts > GROUP_WINDOW;

      const lines = wrap(ctx, m.body, MAX_BUBBLE_W - BPADX * 2);
      let textW = 0;
      for (const l of lines) textW = Math.max(textW, ctx.measureText(l).width);
      const bubbleW = Math.min(MAX_BUBBLE_W, Math.ceil(textW) + BPADX * 2);
      const bubbleH = lines.length * LINE_H + BPADY * 2;

      const showName = groupStart && !mine;
      const day = newDay ? fmtDay(m.ts) : null;
      let height = 0;
      if (day) height += DAY_H;
      if (showName) height += NAME_H;
      height += bubbleH;
      if (groupEnd) height += TIME_H;
      height += groupEnd ? GROUP_GAP : TIGHT_GAP;

      blocks.push({
        msg: m,
        mine,
        lines,
        bubbleW,
        bubbleH,
        showName,
        showTime: groupEnd,
        showAvatar: groupEnd && !mine,
        day,
        top: yAcc,
        height,
      });
      yAcc += height;
    }
    return { blocks, contentH: yAcc };
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);
    drawSceneTitle(ctx, this.title);
    // Header divider.
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fillRect(0, 52, VIRTUAL_WIDTH, 1);

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillStyle = "#9fb0c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Log in to chat.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    const { blocks, contentH } = this.layout(ctx);
    this.maxScroll = Math.max(0, contentH - LIST_H);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;

    if (blocks.length === 0) {
      this.renderEmpty(ctx);
    } else {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, LIST_TOP, VIRTUAL_WIDTH, LIST_H);
      ctx.clip();
      // Anchor newest to the bottom; scrollY lifts the log up into history.
      const contentTop = LIST_BOTTOM - contentH + this.scrollY;
      for (const b of blocks) {
        let y = contentTop + b.top;
        if (y + b.height < LIST_TOP || y > LIST_BOTTOM) continue;
        if (b.day) {
          drawDaySep(ctx, b.day, y + DAY_H / 2);
          y += DAY_H;
        }
        const bx = b.mine ? VIRTUAL_WIDTH - PAD - b.bubbleW : PAD + AV_GUT;
        if (b.showName) {
          ctx.fillStyle = nameColor(b.msg.author);
          ctx.font = "bold 11px system-ui, sans-serif";
          ctx.textAlign = "left";
          ctx.textBaseline = "alphabetic";
          ctx.fillText(b.msg.author, bx + 2, y + 11);
          y += NAME_H;
        }
        const bubbleTop = y;
        drawBubble(ctx, bx, bubbleTop, b.bubbleW, b.bubbleH, b.mine, b.lines);
        if (b.showAvatar) {
          const ax = PAD + AV / 2;
          const ay = bubbleTop + b.bubbleH - AV / 2;
          // The player's equipped character, falling back to an initial bubble.
          if (!drawCharacterAvatar(ctx, b.msg.character ?? null, b.msg.hat ?? null, ax, ay, AV))
            drawAvatar(ctx, b.msg.author, ax, ay);
        }
        y += b.bubbleH;
        if (b.showTime) {
          ctx.fillStyle = TIME_COLOR;
          ctx.font = "10px system-ui, sans-serif";
          ctx.textBaseline = "alphabetic";
          if (b.mine) {
            ctx.textAlign = "right";
            ctx.fillText(fmtTime(b.msg.ts), VIRTUAL_WIDTH - PAD - 2, y + 11);
          } else {
            ctx.textAlign = "left";
            ctx.fillText(fmtTime(b.msg.ts), bx + 2, y + 11);
          }
        }
      }
      ctx.restore();
    }

    // Input bar: subtle top divider so it reads as a docked composer.
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fillRect(0, INPUT_Y - 9, VIRTUAL_WIDTH, 1);
    this.input.render(ctx);

    if (this.toastT > 0) {
      ctx.globalAlpha = Math.min(1, this.toastT);
      ctx.fillStyle = "#1a1f2b";
      const tw = 180;
      fillRoundedRect(ctx, (VIRTUAL_WIDTH - tw) / 2, LIST_TOP + 6, tw, 24, 12);
      ctx.fillStyle = "#ff9a9a";
      ctx.font = "bold 12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(this.toast, VIRTUAL_WIDTH / 2, LIST_TOP + 18);
      ctx.textBaseline = "alphabetic";
      ctx.globalAlpha = 1;
    }
  }

  private renderEmpty(ctx: CanvasRenderingContext2D): void {
    const cy = LIST_TOP + LIST_H / 2;
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.beginPath();
    ctx.arc(VIRTUAL_WIDTH / 2, cy - 18, 26, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#6f7b93";
    ctx.font = "24px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("💬", VIRTUAL_WIDTH / 2, cy - 18);
    ctx.fillStyle = "#aab4c8";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText("No messages yet", VIRTUAL_WIDTH / 2, cy + 18);
    ctx.fillStyle = "#6f7b93";
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillText("Say hi to start the conversation!", VIRTUAL_WIDTH / 2, cy + 38);
    ctx.textBaseline = "alphabetic";
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY - e.deltaY, 0, this.maxScroll);
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    const { y } = this.ctxMgr.renderer.clientToCanvas(e.clientX, e.clientY);
    if (y >= LIST_TOP && y <= LIST_BOTTOM) this.dragLastY = e.clientY;
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.ctxMgr.renderer.canvas.getBoundingClientRect();
    const dy = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY + dy, 0, this.maxScroll);
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };
}

// ---- drawing helpers ----

function drawBubble(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  mine: boolean,
  lines: string[],
): void {
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 2;
  if (mine) {
    const g = ctx.createLinearGradient(0, y, 0, y + h);
    g.addColorStop(0, MINE_TOP);
    g.addColorStop(1, MINE_BOT);
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = OTHER_FILL;
  }
  bubblePath(ctx, x, y, w, h, mine);
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = mine ? MINE_TEXT : OTHER_TEXT;
  ctx.font = "13px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  let ty = y + BPADY + 12;
  for (const l of lines) {
    ctx.fillText(l, x + BPADX, ty);
    ty += LINE_H;
  }
}

function bubblePath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  mine: boolean,
): void {
  const R = 14;
  const tail = 5;
  // [topLeft, topRight, bottomRight, bottomLeft] — the bubble's near-tail corner is tight.
  const radii = mine ? [R, R, tail, R] : [R, R, R, tail];
  const c = ctx as CanvasRenderingContext2D & {
    roundRect?: (x: number, y: number, w: number, h: number, r: number[]) => void;
  };
  if (typeof c.roundRect === "function") {
    ctx.beginPath();
    c.roundRect(x, y, w, h, radii);
  } else {
    roundedRectPath(ctx, x, y, w, h, 12);
  }
}

function drawAvatar(ctx: CanvasRenderingContext2D, name: string, cx: number, cy: number): void {
  const hue = hashHue(name);
  ctx.fillStyle = `hsl(${hue}, 40%, 44%)`;
  ctx.beginPath();
  ctx.arc(cx, cy, AV / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = "bold 14px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((name.trim()[0] ?? "?").toUpperCase(), cx, cy + 1);
  ctx.textBaseline = "alphabetic";
}

function drawDaySep(ctx: CanvasRenderingContext2D, label: string, cy: number): void {
  ctx.font = "bold 10px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const w = ctx.measureText(label).width + 22;
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  fillRoundedRect(ctx, (VIRTUAL_WIDTH - w) / 2, cy - 9, w, 18, 9);
  ctx.fillStyle = "#aab4c8";
  ctx.fillText(label, VIRTUAL_WIDTH / 2, cy + 1);
  ctx.textBaseline = "alphabetic";
}

function drawSendButton(ctx: CanvasRenderingContext2D, hit: boolean): void {
  const cx = SEND.x + SEND.w / 2;
  const cy = SEND.y + SEND.h / 2;
  ctx.fillStyle = hit ? "#e6c860" : "#f0d98a";
  ctx.beginPath();
  ctx.arc(cx, cy, SEND.w / 2, 0, Math.PI * 2);
  ctx.fill();
  // Paper-plane glyph pointing right.
  ctx.fillStyle = "#2a2110";
  ctx.beginPath();
  ctx.moveTo(cx - 8, cy - 7);
  ctx.lineTo(cx + 9, cy);
  ctx.lineTo(cx - 8, cy + 7);
  ctx.lineTo(cx - 4, cy);
  ctx.closePath();
  ctx.fill();
}

function drawJumpButton(ctx: CanvasRenderingContext2D, hit: boolean): void {
  const cx = JUMP.x + JUMP.w / 2;
  const cy = JUMP.y + JUMP.h / 2;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 6;
  ctx.fillStyle = hit ? "#39435e" : "#2b3349";
  ctx.beginPath();
  ctx.arc(cx, cy, JUMP.w / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.strokeStyle = "#cdd6e6";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy - 3);
  ctx.lineTo(cx, cy + 4);
  ctx.lineTo(cx + 5, cy - 3);
  ctx.stroke();
}

function hashHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

function nameColor(name: string): string {
  return `hsl(${hashHue(name)}, 55%, 72%)`;
}

function dayKey(ts: number): string {
  return new Date(ts).toDateString();
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtDay(ts: number): string {
  const k = dayKey(ts);
  const now = new Date();
  if (k === now.toDateString()) return "Today";
  if (k === new Date(now.getTime() - 86400000).toDateString()) return "Yesterday";
  return new Date(ts).toLocaleDateString([], { month: "short", day: "numeric" });
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  let cur = "";
  for (const word of text.split(" ")) {
    const test = cur ? `${cur} ${word}` : word;
    if (ctx.measureText(test).width <= maxW) {
      cur = test;
      continue;
    }
    if (cur) out.push(cur);
    // A single word wider than the line is hard-broken by characters.
    if (ctx.measureText(word).width > maxW) {
      let chunk = "";
      for (const ch of word) {
        if (ctx.measureText(chunk + ch).width > maxW && chunk) {
          out.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      }
      cur = chunk;
    } else {
      cur = word;
    }
  }
  if (cur) out.push(cur);
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
